import {_} from 'lodash';
import {Permission} from './Permission';
import {MAX_RESULTS_PER_PAGE, ObjectTypeId} from '../constants';
import {
    IFetchRouteDef,
    IRouteDefinition,
    TRouteId,
} from '../index';
import {_d} from '../util/misc';

/** Raw route catalog (data only). Lookup/hash helpers live in sibling modules. */

type TFetchKey = 'User'
    | 'Group'
    | 'Department'
    | 'DepartmentHierarchy'
    | 'LegalUnit'
    | 'LegalUnitHierarchy'
    | 'ManualUser'
    | 'APIUser'
    | 'ManualAPIUser'
    | 'ManualGroup'
    | 'ManualDepartment'
    | 'ExternalContact'
    | 'FormData'
    | 'Ruleset'
    | 'Company'
    | 'CompanySyncstep'
    | 'FormTemplate'
    | 'ImportHrFile'
    | 'SystemAccess'
    | 'SystemAccessPermission'
    | 'MessageFlow'
    | 'SyncTemplate'
    ;

/*
To show route name, e.g. Bruker [Ola Nordmann], the following properties must be set:
    - fetchRouteData (true)
    - serviceName ('getobject')
    - objectTypeId
 */

const FetchGetObject: IFetchRouteDef = {serviceName: 'getobject', params: {manual: false}};
const FetchGetObjectManual = _.defaultsDeep({params: {manual: true}}, FetchGetObject);

const Fetch: { [key in TFetchKey]: IFetchRouteDef } = {
    APIUser: FetchGetObject,
    Company: {serviceName: 'getcompany'},
    CompanySyncstep: {serviceName: 'getcompanysyncsteps'},
    Department: FetchGetObject,
    DepartmentHierarchy: {serviceName: 'getorganizationchart'},
    LegalUnit: FetchGetObject,
    LegalUnitHierarchy: {
        serviceName: 'searchobject',
        params: {
            objecttypeid: ObjectTypeId.LegalUnit,
            resultsprpage: MAX_RESULTS_PER_PAGE,
            sortcol: 'SourceId',
        },
    },
    ExternalContact: FetchGetObject,
    FormData: FetchGetObject,
    FormTemplate: {serviceName: 'getformtemplate'},
    Group: FetchGetObject,
    ImportHrFile: {serviceName: 'getimporthrfile'},
    ManualAPIUser: FetchGetObjectManual,
    ManualDepartment: FetchGetObjectManual,
    ManualGroup: FetchGetObjectManual,
    ManualUser: FetchGetObjectManual,
    MessageFlow: {serviceName: 'getmessage'},
    Ruleset: {serviceName: 'getruleset'},
    SyncTemplate: {serviceName: 'getexporttemplate'},
    SystemAccess: {serviceName: 'searchsystem', params: {resultsprpage: MAX_RESULTS_PER_PAGE}},
    SystemAccessPermission: {serviceName: 'searchsystem', params: {resultsprpage: MAX_RESULTS_PER_PAGE}},
    User: FetchGetObject,
} as const;

const RouteDefinition: IRouteDefinition[] = [

    /*******************************************************************************************************************
     * USER
     ******************************************************************************************************************/
    {
        id: 'userstart',
        component: {name: 'component/page-shell/details'},
        create: 'createmanualuser',
        createWizard: 'wizardcreateuser',
        icon: 'fa-user',
        label: 'user|p',
        labelMenu: 'nav-left.section.users-org.users',
        hash: 'o/{orgnr}/user',
        objectTypeId: ObjectTypeId.User,
        permissionLevel: Permission.SuperEmployee,
        showHeadingButtons: false,
        subItems: ['userdetails', 'manualuserdetails'],
        tabs: ['usersearch', 'manualusersearch'],
    },
    {
        id: 'usersearch',
        child: 'userdetails',
        component: {name: 'component/user/search'},
        hash: ['o/{orgnr}/user/search/:options:', 'user'],
        icon: 'fa-user',
        label: 'user|p',
        isSearch: true,
        objectTypeId: ObjectTypeId.User,
        parent: 'userstart',
        permissionLevel: Permission.SuperEmployee,
        serviceName: 'searchobject',
        subItems: null,
    },
    {
        id: 'userdetails',
        canSeeOwnPage: true,
        component: {name: 'component/page-shell/details'},
        content: 'component/page-shell/tab-panels',
        fetchRouteDef: Fetch.User,
        // Bare user/{id}: Router prefixes o/{selectedOrg}/ (hashPrefixOrgId default).
        hash: ['o/{orgnr}/user/{id}/:options:', 'user/{id}/:options:'],
        icon: 'fa-user',
        isSubitem: true,
        label: 'user',
        objectTypeId: ObjectTypeId.User,
        parent: 'userstart',
        permissionLevel: Permission.SuperEmployee,
        tabs: [
            'personal',         // both
            'employment',       // both
            'positions',        // hr
            'userpermissions',  // hr
            'enrollment',       // edu
            // 'education',        // edu
            'changepassword',   // edu
            'membership',       // both
            'usersourcedata',          // both
            'useranchor',       // both
            'userhistory',      // both
            'exportqueue',      // both
            'tasksandmessages', // hr
            'formdata',         // hr
        ],
    },
    {
        id: 'myprofile',
        hash: 'myprofile',
        hashPrefixOrgId: false,
        icon: 'fa-user',
        label: 'my profile',
        permissionLevel: Permission.LoggedIn,
    },
    {
        id: 'mymessages',
        // Alias: Router rewrites to o/{homeOrg}/user/{id}/tasksandmessages when logged in.
        hash: 'mymessages',
        hashPrefixOrgId: false,
        icon: 'fa-user',
        label: 'my messages',
        permissionLevel: Permission.LoggedIn,
    },
    {
        id: 'formdatacontributors',
        canSeeOwnPage: true,
        component: {name: 'component/form/instance/contributors-list', params: {type: 'members'}},
        fetchRouteDef: Fetch.User,
        icon: 'fa-file-invoice',
        label: 'data contributor',
        license: 'ehub',
        objectTypeId: ObjectTypeId.User,
        permissionLevel: Permission.SuperEmployee,
    },
    {
        id: 'userpermissions',
        canSeeOwnPage: true,
        component: {name: 'component/user/permissions-v2/details'},
        datasource: 'hr',
        fetchRouteDef: Fetch.User,
        icon: 'fa-id-card-alt',
        label: 'view.objectdetail.user.permissions',
        objectTypeId: ObjectTypeId.User,
        parent: 'userdetails',
        permissionLevel: Permission.SuperEmployee,
    },
    {
        id: 'employment',
        canSeeOwnPage: true,
        component: {name: 'component/user/syncfields/employment'},
        fetchRouteDef: Fetch.User,
        icon: 'fa-employement',
        // not used in edu for now
        datasource: 'hr',
        // identityType: 'Staff',
        label: 'employment',
        objectTypeId: ObjectTypeId.User,
        permissionLevel: Permission.SuperEmployee,
    },
    {
        id: 'membership',
        canSeeOwnPage: true,
        child: 'groupdetails',
        component: {name: 'component/user/membership-v2/details'},
        icon: 'fa-users',
        label: 'membership',
        labelLong: 'group membership',
        objectTypeId: ObjectTypeId.User,
        permissionLevel: Permission.DepartmentAdmin,
    },
    {
        id: 'personal',
        canSeeOwnPage: true,
        component: {name: 'component/user/syncfields/personal'},
        fetchRouteDef: Fetch.User,
        icon: 'fa-user-info',
        label: 'personal data',
        objectTypeId: ObjectTypeId.User,
        permissionLevel: Permission.SuperEmployee,
    },
    {
        id: 'positions',
        canSeeOwnPage: true,
        component: {name: 'component/user/syncfields/positions-page'},
        fetchRouteDef: Fetch.User,
        icon: 'fa-suitcase',
        identityType: 'Staff',
        label: 'position|p',
        objectTypeId: ObjectTypeId.User,
        permissionLevel: Permission.SuperEmployee,
    },
    {
        id: 'enrollment',
        canSeeOwnPage: true,
        component: {name: 'component/user/syncfields/enrollment'},
        datasource: 'edu',
        fetchRouteDef: Fetch.User,
        identityType: 'Learner',
        label: 'Enrollment',
        objectTypeId: ObjectTypeId.User,
        permissionLevel: Permission.SuperEmployee,
    },
    {
        id: 'changepassword',
        canSeeOwnPage: false,
        component: {name: 'component/password/v2/details', params: {mode: 'admin'}},
        datasource: 'edu',
        identityType: 'Learner',
        label: 'Change password',
        objectTypeId: ObjectTypeId.User,
        showHeading: false,
    },
    {
        id: 'tasksandmessages',
        canSeeOwnPage: true,
        component: {name: 'component/user/messages/details'},
        datasource: 'hr',
        fetchRouteDef: Fetch.User,
        icon: 'fa-clipboard-check',
        label: 'task|p',
        labelLong: 'tasks and notifications',
        objectTypeId: ObjectTypeId.User,
        permissionLevel: Permission.DepartmentAdmin,
    },
    {
        id: 'formdata',
        _extend: 'formdatacontributors',
        component: {params: {type: 'formdata'}},
        datasource: 'hr',
        label: 'formdata',
        labelLong: 'formdata',
        permissionLevel: Permission.DepartmentAdmin,
    },

    /*******************************************************************************************************************
     * GROUP
     ******************************************************************************************************************/
    {
        id: 'groupstart',
        component: {name: 'component/page-shell/details'},
        create: 'createmanualgroup',
        createWizard: 'wizardcreategroup',
        icon: 'fa-users',
        label: 'group|p',
        labelMenu: 'nav-left.section.users-org.groups',
        hash: 'o/{orgnr}/group',
        objectTypeId: ObjectTypeId.Group,
        permissionLevel: Permission.SuperManager,
        showHeadingButtons: false,
        subItems: ['groupdetails', 'manualgroupdetails'],
        tabs: ['groupsearch', 'manualgroupsearch'],
    },
    {
        id: 'groupsearch',
        child: 'groupdetails',
        component: {name: 'component/object/search'},
        hash: 'o/{orgnr}/group/search',
        icon: 'fa-users',
        label: 'group|p',
        isSearch: true,
        objectTypeId: ObjectTypeId.Group,
        parent: 'groupstart',
        permissionLevel: Permission.SuperManager,
        serviceName: 'searchobject',
        subItems: null,
    },
    {
        id: 'groupdetails',
        component: {name: 'component/page-shell/details'},
        content: 'component/page-shell/tab-panels',
        fetchRouteDef: Fetch.Group,
        hash: 'o/{orgnr}/group/{id}',
        icon: 'fa-users',
        isSubitem: true,
        label: 'group',
        objectTypeId: ObjectTypeId.Group,
        parent: 'groupstart',
        permissionLevel: Permission.SuperManager,
        tabs: ['groupsourcedata', 'groupmembers', 'groupanchor', 'grouphistory', 'groupusedin'],
    },
    {
        id: 'groupmembers',
        component: {name: 'component/group/members/details'},
        fetchRouteDef: Fetch.Group,
        icon: 'fa-members',
        label: 'member|p',
        labelLong: 'group member|p',
        objectTypeId: ObjectTypeId.User,
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'groupusedin',
        component: {name: 'component/group/used-in'},
        fetchRouteDef: Fetch.SystemAccess,
        icon: 'fa-server-check-circle',
        label: 'used in',
        labelLong: 'used in subject|subject:((access control|ac))',
        permissionLevel: Permission.SuperManager,
    },
    /*******************************************************************************************************************
     * DEPARTMENT
     ******************************************************************************************************************/
    {
        id: 'departmentstart',
        component: {name: 'component/page-shell/details'},
        create: 'createmanualdepartment',
        icon: 'fa-sitemap',
        label: 'department|p',
        labelMenu: 'nav-left.section.users-org.departments',
        hash: 'o/{orgnr}/department',
        objectTypeId: ObjectTypeId.Department,
        permissionLevel: Permission.SuperEmployee,
        showHeadingButtons: false,
        subItems: ['departmentdetails', 'manualdepartmentdetails'],
        tabs: [
            'departmenthierarchy',
            {_extend: 'departmentsearch', icon: 'fa-sitemap'},
            {_extend: 'manualdepartmentsearch', icon: 'fa-manual-department'},
        ],
    },
    {
        id: 'departmenthierarchy',
        child: 'departmentdetails',
        component: {name: 'component/department/hierarchy'},
        fetchRouteDef: Fetch.DepartmentHierarchy,
        // Canonical: o/{orgnr}/…/:id:. Standalone + double-orgnr (duporgnr) kept for bookmarks / win.html.
        hash: [
            'o/{orgnr}/departmenthierarchy/:id:',
            'departmenthierarchy/:orgnr:/:id:',
            'o/{orgnr}/departmenthierarchy/{duporgnr}/:id:',
        ],
        hashPrefixOrgId: false,
        icon: 'fa-folder-tree',
        label: 'Hierarchy',
        labelLong: 'Department hierarchy',
        parent: 'departmentstart',
        permissionLevel: Permission.None,
        subItems: null,
    },
    {
        id: 'departmentsearch',
        child: 'departmentdetails',
        component: {name: 'component/object/search'},
        hash: ['o/{orgnr}/department/search', 'o/{orgnr}/departmentsearch'],
        icon: 'fa-sitemap',
        label: 'department|p',
        isSearch: true,
        objectTypeId: ObjectTypeId.Department,
        parent: 'departmentstart',
        permissionLevel: Permission.SuperEmployee,
        serviceName: 'searchobject',
        subItems: null,
    },
    {
        id: 'departmentdetails',
        component: {name: 'component/page-shell/details'},
        content: 'component/page-shell/tab-panels',
        fetchRouteDef: Fetch.Department,
        hash: 'o/{orgnr}/department/{id}',
        icon: 'fa-sitemap',
        isSubitem: true,
        label: 'department',
        objectTypeId: ObjectTypeId.Department,
        parent: 'departmentstart',
        permissionLevel: Permission.SuperEmployee,
        tabs: ['departmentsourcedata', 'departmentemployees', 'departmenthierarchy', 'departmentanchor', 'departmenthistory'],
    },

    /*******************************************************************************************************************
     * LEGAL UNIT (BRREG)
     ******************************************************************************************************************/
    {
        id: 'legalunitstart',
        component: {name: 'component/page-shell/details'},
        icon: 'fa-building',
        label: 'legalunit|p',
        labelMenu: 'nav-left.section.users-org.legalunits',
        hash: 'o/{orgnr}/legalunit',
        license: 'brreg',
        objectTypeId: ObjectTypeId.LegalUnit,
        permissionLevel: Permission.CompanyAdmin,
        showHeadingButtons: false,
        subItems: ['legalunitdetails'],
        tabs: ['legalunithierarchy', 'legalunitsearch'],
    },
    {
        id: 'legalunithierarchy',
        child: 'legalunitdetails',
        component: {name: 'component/legal-unit/hierarchy'},
        fetchRouteDef: Fetch.LegalUnitHierarchy,
        hash: 'o/{orgnr}/legalunit/hierarchy/:id:',
        icon: 'fa-folder-tree',
        label: 'Hierarchy',
        labelLong: 'legalunit.hierarchy',
        license: 'brreg',
        objectTypeId: ObjectTypeId.LegalUnit,
        parent: 'legalunitstart',
        permissionLevel: Permission.CompanyAdmin,
        subItems: null,
    },
    {
        id: 'legalunitsearch',
        child: 'legalunitdetails',
        component: {name: 'component/object/search'},
        hash: 'o/{orgnr}/legalunit/search',
        icon: 'fa-building',
        isSearch: true,
        label: 'legalunit|p',
        license: 'brreg',
        objectTypeId: ObjectTypeId.LegalUnit,
        parent: 'legalunitstart',
        permissionLevel: Permission.CompanyAdmin,
        serviceName: 'searchobject',
        subItems: null,
    },
    {
        id: 'legalunitdetails',
        component: {name: 'component/page-shell/details'},
        content: 'component/page-shell/tab-panels',
        fetchRouteDef: Fetch.LegalUnit,
        hash: 'o/{orgnr}/legalunit/{id}',
        icon: 'fa-building',
        isSubitem: true,
        label: 'legalunit',
        license: 'brreg',
        objectTypeId: ObjectTypeId.LegalUnit,
        parent: 'legalunitstart',
        permissionLevel: Permission.CompanyAdmin,
        tabs: ['legalunitsourcedata', 'legalunithierarchy', 'legalunithistory'],
    },
    {
        id: 'legalunitsourcedata',
        _extend: '_sourcedata',
        fetchRouteDef: Fetch.LegalUnit,
        license: 'brreg',
        objectTypeId: ObjectTypeId.LegalUnit,
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'legalunithistory',
        _extend: '_history',
        fetchRouteDef: Fetch.LegalUnit,
        license: 'brreg',
        objectTypeId: ObjectTypeId.LegalUnit,
        permissionLevel: Permission.CompanyAdmin,
    },

    /*******************************************************************************************************************
     * OBJECT
     ******************************************************************************************************************/
    {
        id: '_anchor',
        component: {name: 'component/object/anchor'},
        icon: 'fa-anchor',
        label: 'anchor',
        permissionLevel: Permission.SuperEmployee,
    },
    {
        id: 'useranchor',
        _extend: '_anchor',
        fetchRouteDef: Fetch.User,
        objectTypeId: ObjectTypeId.User,
        permissionLevel: Permission.DepartmentAdmin,
    },
    {
        id: 'groupanchor',
        _extend: '_anchor',
        fetchRouteDef: Fetch.Group,
        objectTypeId: ObjectTypeId.Group,
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'departmentanchor',
        _extend: '_anchor',
        fetchRouteDef: Fetch.Department,
        objectTypeId: ObjectTypeId.Department,
    },
    {
        id: '_history',
        component: {name: 'component/object/history/instance'},
        icon: 'fa-history',
        label: 'history',
        permissionLevel: Permission.DepartmentAdmin,
    },
    {
        id: 'userhistory',
        _extend: '_history',
        fetchRouteDef: Fetch.User,
        objectTypeId: ObjectTypeId.User,
    },
    {
        id: 'exportqueue',
        canSeeOwnPage: true,
        component: {name: 'component/user/export-queue'},
        fetchRouteDef: Fetch.User,
        icon: 'fa-user-time',
        label: 'password history',
        permissionLevel: Permission.DepartmentAdmin,
    },
    {
        id: 'grouphistory',
        _extend: '_history',
        fetchRouteDef: Fetch.Group,
        objectTypeId: ObjectTypeId.Group,
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'departmenthistory',
        _extend: '_history',
        fetchRouteDef: Fetch.Department,
        objectTypeId: ObjectTypeId.Department,
    },
    {
        id: 'departmentemployees',
        child: 'userdetails',
        component: {name: 'component/user/search.by.department'},
        fetchRouteDef: Fetch.Department,
        icon: 'fa-department-user',
        label: 'employee|p',
        isSearch: true,
        objectTypeId: ObjectTypeId.User,
        permissionLevel: Permission.SuperEmployee,
        serviceName: 'searchobject',
    },
    {
        id: '_sourcedata',
        component: {name: 'component/object/raw'},
        icon: 'fa-object-raw',
        label: 'source data',
        permissionLevel: Permission.SuperEmployee,
    },
    {
        id: 'usersourcedata',
        _extend: '_sourcedata',
        canSeeOwnPage: true,
        fetchRouteDef: Fetch.User,
        objectTypeId: ObjectTypeId.User,
        permissionLevel: Permission.DepartmentAdmin,
    },
    {
        id: 'groupsourcedata',
        _extend: '_sourcedata',
        fetchRouteDef: Fetch.Group,
        objectTypeId: ObjectTypeId.Group,
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'departmentsourcedata',
        _extend: '_sourcedata',
        fetchRouteDef: Fetch.Department,
        objectTypeId: ObjectTypeId.Department,
    },
    {
        id: '_manualsourcedata',
        _extend: '_sourcedata',
        labelLong: 'manual source data',
        manual: true,
    },
    {
        id: 'manualusersourcedata',
        _extend: '_manualsourcedata',
        canSeeOwnPage: false,
        fetchRouteDef: Fetch.ManualUser,
        objectTypeId: ObjectTypeId.User,
    },
    {
        id: 'manualgroupsourcedata',
        _extend: '_manualsourcedata',
        fetchRouteDef: Fetch.ManualGroup,
        objectTypeId: ObjectTypeId.Group,
    },
    {
        id: 'manualdepartmentsourcedata',
        _extend: '_manualsourcedata',
        fetchRouteDef: Fetch.ManualDepartment,
        objectTypeId: ObjectTypeId.Department,
    },
    {
        id: 'formdatasourcedata',
        _extend: '_sourcedata',
        fetchRouteDef: Fetch.FormData,
        objectTypeId: ObjectTypeId.FormData,
    },
    /*******************************************************************************************************************
     * MANUAL OBJECT
     ******************************************************************************************************************/
    {
        id: 'manualusersearch',
        child: 'manualuserdetails',
        component: {name: 'component/user/search'},
        create: 'createmanualuser',
        createWizard: 'wizardcreateuser',
        customButtons: ['uploadmanualusers'],
        hash: ['o/{orgnr}/manualuser/search', 'o/{orgnr}/manualuser'],
        icon: 'fa-manual-user',
        label: 'source data manual user|p',
        isSearch: true,
        manual: true,
        objectTypeId: ObjectTypeId.User,
        parent: 'userstart',
        permissionLevel: Permission.SuperManager,
        serviceName: 'searchobject',
        subItems: null,
    },
    {
        id: 'manualuserdetails',
        canSeeOwnPage: false,
        component: {name: 'component/page-shell/details'},
        content: 'component/page-shell/tab-panels',
        edit: 'editmanualuser',
        fetchRouteDef: Fetch.ManualUser,
        hash: 'o/{orgnr}/manualuser/{id}',
        icon: 'fa-manual-user',
        isSubitem: true,
        label: 'source data manual user',
        manual: true,
        objectTypeId: ObjectTypeId.User,
        parent: 'userstart',
        permissionLevel: Permission.SuperManager,
        showTabs: false,
        tabs: ['manualusersourcedata'],
    },
    {
        id: 'manualgroupsearch',
        component: {name: 'component/group/search'},
        child: 'manualgroupdetails',
        create: 'createmanualgroup',
        createWizard: 'wizardcreategroup',
        customButtons: ['uploadmanualgroups'],
        icon: 'fa-manual-group',
        label: 'source data manual group|p',
        isSearch: true,
        hash: ['o/{orgnr}/manualgroup/search', 'o/{orgnr}/manualgroup'],
        manual: true,
        objectTypeId: ObjectTypeId.Group,
        parent: 'groupstart',
        permissionLevel: Permission.SuperManager,
        serviceName: 'searchobject',
        subItems: null,
    },
    {
        id: 'manualgroupdetails',
        component: {name: 'component/page-shell/details'},
        content: 'component/page-shell/tab-panels',
        edit: 'editmanualgroup',
        fetchRouteDef: Fetch.ManualGroup,
        hash: 'o/{orgnr}/manualgroup/{id}',
        icon: 'fa-manual-group',
        isSubitem: true,
        label: 'source data manual group',
        manual: true,
        objectTypeId: ObjectTypeId.Group,
        parent: 'groupstart',
        permissionLevel: Permission.SuperManager,
        showTabs: false,
        tabs: ['manualgroupsourcedata'],
    },
    {
        id: 'manualdepartmentsearch',
        component: {name: 'component/object/search'},
        child: 'manualdepartmentdetails',
        create: 'createmanualdepartment',
        customButtons: ['uploadmanualdepartments'],
        hash: ['o/{orgnr}/manualdepartment/search', 'o/{orgnr}/manualdepartment'],
        icon: 'fa-manual-department',
        label: 'source data manual department|p',
        isSearch: true,
        manual: true,
        objectTypeId: ObjectTypeId.Department,
        parent: 'departmentstart',
        permissionLevel: Permission.SuperManager,
        serviceName: 'searchobject',
        subItems: null,
    },
    {
        id: 'manualdepartmentdetails',
        component: {name: 'component/page-shell/details'},
        content: 'component/page-shell/tab-panels',
        edit: 'editmanualdepartment',
        fetchRouteDef: Fetch.ManualDepartment,
        hash: 'o/{orgnr}/manualdepartment/{id}',
        icon: 'fa-manual-department',
        isSubitem: true,
        label: 'source data manual department',
        manual: true,
        objectTypeId: ObjectTypeId.Department,
        parent: 'departmentstart',
        permissionLevel: Permission.SuperManager,
        showTabs: false,
        tabs: ['manualdepartmentsourcedata'],
    },
    {
        id: '_wizarduser',
        manual: true,
        objectTypeId: ObjectTypeId.User,
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'wizardcreateuser',
        _extend: '_wizarduser',
        component: {name: 'component/user/wizard/create'},
        hash: 'o/{orgnr}/wizardcreateuser',
        icon: 'fa-map-signs',
        label: 'wizard for creating|subject:((manual user|ac))',
        parent: 'userstart',
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'wizardedituser',
        _extend: '_wizarduser',
        component: {name: 'component/user/wizard/edit'},
        fetchRouteDef: Fetch.ManualUser,
        label: 'wizard for editing|subject:((manual user|ac))',
        hash: 'o/{orgnr}/wizardedituser/{id}',
        icon: 'fa-map-signs',
        parent: 'manualuserdetails',
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'wizardcreategroup',
        component: {name: 'component/group/create-wizard'},
        manual: true,
        hash: 'o/{orgnr}/wizardcreategroup',
        icon: 'fa-map-signs',
        label: 'wizard for creating|subject:((manual group|ac))',
        objectTypeId: ObjectTypeId.Group,
        parent: 'groupstart',
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'wizardeditgroup',
        component: {name: 'component/group/edit-wizard'},
        manual: true,
        hash: 'o/{orgnr}/wizardeditgroup/{id}',
        icon: 'fa-map-signs',
        label: 'wizard for editing|subject:((manual group|ac))',
        objectTypeId: ObjectTypeId.Group,
        parent: 'groupstart',
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: '_uploadmanualobjects',
        component: {name: 'component/object/upload/objects'},
        icon: 'fa-upload',
        manual: true,
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'uploadmanualusers',
        _extend: '_uploadmanualobjects',
        hash: 'o/{orgnr}/uploadmanualusers',
        label: 'wizard for uploading|subject:((manual user|p|ac))',
        objectTypeId: ObjectTypeId.User,
        parent: 'userstart',
    },
    {
        id: 'uploadmanualgroups',
        _extend: '_uploadmanualobjects',
        hash: 'o/{orgnr}/uploadmanualgroups',
        label: 'wizard for uploading|subject:((manual group|p|ac))',
        objectTypeId: ObjectTypeId.Group,
        parent: 'groupstart',
    },
    {
        id: 'uploadmanualdepartments',
        _extend: '_uploadmanualobjects',
        hash: 'o/{orgnr}/uploadmanualdepartments',
        label: 'wizard for uploading|subject:((manual department|p|ac))',
        objectTypeId: ObjectTypeId.Department,
        parent: 'departmentstart',
    },
    {
        id: '_createobject',
        component: {name: 'component/page-shell/details'},
        content: 'component/object/create',
        icon: 'fa-plus',
        manual: true,
        permissionLevel: Permission.CompanyAdmin,
        tabMemory: '_createobject',
        tabs: ['syncfieldsmandatory', 'syncfieldsoptional'],
    },
    {
        id: 'createmanualuser',
        _extend: '_createobject',
        hash: 'o/{orgnr}/createmanualuser',
        label: 'manualobject.user.create',
        objectTypeId: ObjectTypeId.User,
        parent: 'userstart',
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'createmanualgroup',
        _extend: '_createobject',
        hash: 'o/{orgnr}/createmanualgroup',
        label: 'manualobject.group.create',
        objectTypeId: ObjectTypeId.Group,
        parent: 'groupstart',
    },
    {
        id: 'createmanualdepartment',
        _extend: '_createobject',
        hash: 'o/{orgnr}/createmanualdepartment',
        label: 'manualobject.department.create',
        objectTypeId: ObjectTypeId.Department,
        parent: 'departmentstart',
    },
    {
        id: '_editobject',
        component: {name: 'component/page-shell/details'},
        content: 'component/object/edit',
        icon: 'fa-pencil',
        manual: true,
        permissionLevel: Permission.CompanyAdmin,
        tabs: ['syncfieldsmandatory', 'syncfieldsoptional']
    },
    {
        id: 'syncfieldsmandatory',
        icon: 'fa-exclamation-circle',
        label: 'mandatory field|p',
        labelLong: 'mandatory synchronization field|p',
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'syncfieldsoptional',
        icon: 'fa-question-circle',
        label: 'optional field|p',
        labelLong: 'optional synchronization field|p',
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'editmanualuser',
        _extend: '_editobject',
        fetchRouteDef: Fetch.ManualUser,
        hash: 'o/{orgnr}/editmanualuser/{id}',
        label: 'manualobject.user.edit',
        objectTypeId: ObjectTypeId.User,
        parent: 'manualuserdetails',
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'editmanualgroup',
        _extend: '_editobject',
        fetchRouteDef: Fetch.ManualGroup,
        hash: 'o/{orgnr}/editmanualgroup/{id}',
        label: 'manualobject.group.edit',
        objectTypeId: ObjectTypeId.Group,
        parent: 'manualgroupdetails',
    },
    {
        id: 'editmanualdepartment',
        _extend: '_editobject',
        fetchRouteDef: Fetch.ManualDepartment,
        hash: 'o/{orgnr}/editmanualdepartment/{id}',
        label: 'manualobject.department.edit',
        objectTypeId: ObjectTypeId.Department,
        parent: 'manualdepartmentdetails',
    },

    /*******************************************************************************************************************
     * API USER
     ******************************************************************************************************************/
    {
        id: 'apiuserstart',
        component: {name: 'component/page-shell/details'},
        icon: 'fa-user-robot',
        label: 'APIuser|p',
        labelMenu: 'nav-left.section.automation.apiusers',
        hash: 'o/{orgnr}/apiuser',
        license: 'apiusers',
        objectTypeId: ObjectTypeId.ApiUser,
        permissionLevel: Permission.CompanyAdmin,
        subItems: ['apiuserdetails', 'manualapiuserdetails'],
        tabs: ['apiusersearch', 'manualapiusersearch'],
    },
    {
        id: 'apiusersearch',
        component: {name: 'component/object/search'},
        child: 'apiuserdetails',
        hash: ['o/{orgnr}/apiuser/search/:options:'],
        icon: 'fa-user-robot',
        label: 'APIuser|p',
        isSearch: true,
        license: 'apiusers',
        manual: false,
        objectTypeId: ObjectTypeId.ApiUser,
        parent: 'apiuserstart',
        permissionLevel: Permission.CompanyAdmin,
        serviceName: 'searchobject',
        subItems: null,
    },
    {
        id: 'manualapiusersearch',
        _extend: 'apiusersearch',
        child: 'manualapiuserdetails',
        create: 'createmanualapiuser',
        hash: ['o/{orgnr}/manualapiuser/search', 'o/{orgnr}/manualapiuser'],
        icon: 'fa-manual-apiuser',
        label: 'Source data manual API user|p',
        labelLong: 'Source data manual API user|p',
        license: 'apiusers',
        manual: true,
    },
    {
        id: 'apiuserdetails',
        canSeeOwnPage: false,
        component: {name: 'component/page-shell/details'},
        content: 'component/page-shell/tab-panels',
        fetchRouteDef: Fetch.APIUser,
        hash: 'o/{orgnr}/apiuser/{id}',
        icon: 'fa-user-robot',
        isSubitem: true,
        label: 'APIuser',
        license: 'apiusers',
        manual: false,
        objectTypeId: ObjectTypeId.ApiUser,
        parent: 'apiuserstart',
        permissionLevel: Permission.CompanyAdmin,
        tabs: [
            'apiusersourcedata',
            'apiuserpermissions',
        ],
    },
    {
        id: 'apiuserpermissions',
        _extend: 'userpermissions',
        canSeeOwnPage: false,
        fetchRouteDef: Fetch.APIUser,
        icon: 'fa-id-card-alt',
        label: 'permission|p',
        license: 'apiusers',
        objectTypeId: ObjectTypeId.ApiUser,
        parent: 'apiuserdetails',
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'manualapiuserdetails',
        _extend: 'apiuserdetails',
        fetchRouteDef: Fetch.ManualAPIUser,
        hash: 'o/{orgnr}/manualapiuser/{id}',
        manual: true,
        edit: 'editmanualapiuser',
        icon: 'fa-manual-apiuser',
        isSubitem: true,
        labelLong: 'Source data manual API user',
        license: 'apiusers',
        objectTypeId: ObjectTypeId.ApiUser,
        parent: 'apiuserstart',
        permissionLevel: Permission.CompanyAdmin,
        showTabs: false,
        tabs: ['manualapiusersourcedata'],
    },
    {
        id: 'manualapiusersourcedata',
        _extend: '_manualsourcedata',
        canSeeOwnPage: false,
        fetchRouteDef: Fetch.ManualAPIUser,
        license: 'apiusers',
        objectTypeId: ObjectTypeId.ApiUser,
    },
    {
        id: 'editmanualapiuser',
        _extend: '_editobject',
        fetchRouteDef: Fetch.ManualAPIUser,
        hash: 'o/{orgnr}/editmanualapiuser/{id}',
        label: 'edit subject|s:((APIuser))',
        license: 'apiusers',
        manual: true,
        objectTypeId: ObjectTypeId.ApiUser,
        parent: 'manualapiuserdetails',
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'createmanualapiuser',
        _extend: '_createobject',
        hash: 'o/{orgnr}/createmanualapiuser',
        label: 'manualobject.apiuser.create',
        license: 'apiusers',
        manual: true,
        objectTypeId: ObjectTypeId.ApiUser,
        parent: 'apiuserstart',
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'apiusersourcedata',
        _extend: '_sourcedata',
        canSeeOwnPage: false,
        fetchRouteDef: Fetch.APIUser,
        license: 'apiusers',
        manual: true,
        objectTypeId: ObjectTypeId.ApiUser,
    },

    /*******************************************************************************************************************
     * PASSWORD
     ******************************************************************************************************************/
    {
        id: 'forgotpassword',
        canSeeAfterLogin: false,
        component: {name: 'component/password/forgot/details'},
        label: 'forgot password',
        hash: [
            'forgotpassword/:orgnr:',
            'o/{orgnr}/forgotpassword',
            'o/{orgnr}/forgotpassword/{duporgnr}',
            'pw/:orgnr:',
            'o/{orgnr}/pw',
        ],
        hashPrefixOrgId: false,
        icon: 'fa-unlock-alt',
        permissionLevel: Permission.None,
        storeHash: false
    },
    {
        id: 'resetpassword',
        component: {name: 'component/password/v2/details', params: {mode: 'admin'}},
        fetchRouteDef: Fetch.User,
        hash: 'o/{orgnr}/resetpassword/{id}',
        icon: 'fa-lock',
        label: 'edit password',
        objectTypeId: ObjectTypeId.User,
        parent: 'userdetails',
        permissionLevel: Permission.SuperEmployee,
        storeHash: false,
    },
    {
        id: 'resetapiuserpassword',
        component: {name: 'component/password/v2/details', params: {mode: 'admin'}},
        fetchRouteDef: Fetch.APIUser,
        hash: 'o/{orgnr}/resetapiuserpassword/{id}',
        icon: 'fa-lock',
        label: 'edit password',
        objectTypeId: ObjectTypeId.ApiUser,
        parent: 'apiuserdetails',
        permissionLevel: Permission.CompanyAdmin,
        storeHash: false,
    },
    {
        id: 'resetownpassword',
        canSeeOwnPage: true,
        component: {name: 'component/password/v2/details'},
        hash: ['o/{orgnr}/resetownpassword/:id:', 'resetownpassword/:id:'],
        hashPrefixOrgId: false,
        icon: 'fa-lock',
        label: 'change password',
        parent: 'userstart',
        permissionLevel: Permission.LoggedIn,
        storeHash: false,
    },

    /*******************************************************************************************************************
     * RULESET
     ******************************************************************************************************************/
    {
        id: 'rulesetsearch',
        component: {name: 'component/ruleset/search-select'},
        child: 'rulesetdetails',
        create: 'createruleset',
        icon: 'fa-ruleset',
        label: 'ruleset|p',
        labelMenu: 'nav-left.section.automation.rulesets',
        isSearch: true,
        hash: ['o/{orgnr}/ruleset/search', 'o/{orgnr}/ruleset'],
        objectTypeId: ObjectTypeId.Ruleset,
        permissionLevel: Permission.SuperManager,
        serviceName: 'searchruleset',
        showHeadingButtons: false,
        subItems: null,
    },
    {
        id: 'rulesetdetails',
        component: {name: 'component/page-shell/details'},
        content: 'component/ruleset/read/details',
        edit: 'editruleset',
        fetchRouteDef: Fetch.Ruleset,
        hash: 'o/{orgnr}/ruleset/{id}',
        icon: 'fa-ruleset',
        isSubitem: true,
        label: 'ruleset',
        objectTypeId: ObjectTypeId.Ruleset,
        parent: 'rulesetsearch',
        permissionLevel: Permission.SuperManager,
        showHeadingButtons: false,
        tabDefault: 'rulesetrules',
        tabs: ['rulesetinfo', 'rulesetrules'],
    },
    {
        id: 'rulesetdetailsorg',
        _extend: 'rulesetdetails',
        hash: ['ruleset/{orgnr}/{id}', 'o/{orgnr}/ruleset/{id}'],
    },
    {
        id: 'rulesetinfo',
        icon: 'fa-info-circle',
        label: 'information',
        level: 2,
        permissionLevel: Permission.SuperManager,
        showHeadingButtons: false,
    },
    {
        id: 'rulesetrules',
        icon: 'fa-list-alt',
        label: 'rule|p',
        level: 2,
        permissionLevel: Permission.SuperManager,
    },
    {
        id: '_mutableruleset',
        permissionLevel: Permission.CompanyAdmin,
        tabMemory: 'rulesetdetails',
        tabs: ['rulesetinfo', 'rulesetrules'],
    },
    {
        id: 'createruleset',
        _extend: '_mutableruleset',
        component: {name: 'component/page-shell/details'},
        content: 'component/ruleset/mutable/create',
        hash: 'o/{orgnr}/createruleset',
        icon: 'fa-plus',
        label: 'create s|s:((ruleset|ac))',
        parent: 'rulesetsearch',
    },
    {
        id: 'editruleset',
        _extend: '_mutableruleset',
        component: {name: 'component/page-shell/details'},
        content: 'component/ruleset/mutable/edit',
        fetchRouteDef: Fetch.Ruleset,
        hash: 'o/{orgnr}/editruleset/{id}',
        icon: 'fa-pencil',
        label: 'edit subject|s:((ruleset|ac))',
        parent: 'rulesetdetails',
        tabDefault: 'rulesetrules',
    },

    /*******************************************************************************************************************
     * ORGANIZATION
     ******************************************************************************************************************/
    {
        id: 'orginfo',
        _extend: 'privacypolicy',
        hash: 'o/{orgnr}/orginfo',
        icon: 'fa-info-circle',
        label: 'privacy.and.agreements',
        permissionLevel: Permission.LoggedIn,
    },
    {
        id: '_customercomments',
        fetchRouteDef: Fetch.Company,
        permissionLevel: Permission.OrganizationAdmin,
    },
    {
        id: 'customercommentsdetails',
        _extend: '_customercomments',
        component: {name: 'component/customer/comments/details'},
        edit: 'editcustomercomments',
        icon: 'fa-sticky-note',
        label: 'comment|p',
        hash: 'o/{orgnr}/customercomments',
    },
    {
        id: 'editcustomercomments',
        _extend: '_customercomments',
        component: {name: 'component/customer/comments/edit'},
        icon: 'fa-pencil',
        label: 'edit subject|s:((comment|p|ac))',
        hash: 'o/{orgnr}/editcustomercomments',
        parent: 'customercommentsdetails',
    },
    {
        id: '_customer',
        component: {name: 'component/page-shell/details'},
        permissionLevel: Permission.OrganizationAdmin,
        tabMemory: 'customerconfig',
        tabs: ['customerinfo', 'customerimport', 'customerapiaccess', 'customergroups', 'customeraccounts', 'customercommunication', 'customerpassword', 'customerpermission', 'customersso'],
    },
    {
        id: 'customerconfig',
        _extend: '_customer',
        content: 'component/customer/general/details',
        create: 'createcustomer',
        customButtons: ['customercommentsdetails'],
        edit: 'editcustomer',
        fetchRouteDef: Fetch.Company,
        hash: 'o/{orgnr}/customerconfig',
        icon: 'fa-customer-config',
        label: 'customer setup',
        labelMenu: 'nav-left.section.sysadmin.customersetup',
    },
    {
        id: 'createcustomer',
        _extend: '_customer',
        content: 'component/customer/general/create',
        hash: 'createcustomer',
        hashPrefixOrgId: false,
        icon: 'fa-plus',
        label: 'create s|s:((customer setup|ac))',
    },
    {
        id: 'editcustomer',
        _extend: '_customer',
        content: 'component/customer/general/edit',
        fetchRouteDef: Fetch.Company,
        hash: 'o/{orgnr}/editcustomer',
        icon: 'fa-pencil',
        label: 'edit subject|s:((customer setup|ac))',
        parent: 'customerconfig',
    },
    {id: 'customerinfo', label: 'information', icon: 'fa-info-circle', permissionLevel: Permission.OrganizationAdmin},
    {id: 'customerimport', label: 'import', icon: 'fa-cloud-download ', permissionLevel: Permission.OrganizationAdmin},
    {
        id: 'customerapiaccess',
        label: 'apiaccess',
        icon: 'fa-server-lock',
        permissionLevel: Permission.OrganizationAdmin
    },
    {id: 'customergroups', label: 'group|p', icon: 'fa-users', permissionLevel: Permission.OrganizationAdmin},
    {
        id: 'customeraccounts',
        label: 'account|p',
        icon: 'fa-user-circle',
        permissionLevel: Permission.OrganizationAdmin
    },
    {
        id: 'customercommunication',
        label: 'communication',
        icon: 'fa-satellite-dish',
        permissionLevel: Permission.OrganizationAdmin
    },
    {
        id: 'customerpassword',
        label: 'forgot password',
        icon: 'fa-lock-cog',
        permissionLevel: Permission.OrganizationAdmin
    },
    {
        id: 'customerpermission',
        label: 'permission|p',
        icon: 'fa-id-card-alt',
        permissionLevel: Permission.OrganizationAdmin
    },
    {
        id: 'customersso',
        label: 'sso.tab',
        icon: 'fa-key',
        permissionLevel: Permission.OrganizationAdmin
    },

    /*******************************************************************************************************************
     * SUPER ADMIN
     ******************************************************************************************************************/
    {
        id: 'report',
        component: {name: 'component/report/details'},
        hash: 'o/{orgnr}/reportsyncstatus',
        hashPrefixOrgId: false,
        icon: 'fa-chart-pie',
        label: 'report',
        labelMenu: 'nav-left.section.sysadmin.report',
        permissionLevel: Permission.OrganizationAdmin,
    },
    {
        id: 'systemfieldsdetails',
        component: {name: 'component/systemfields/details'},
        hash: 'systemfields',
        hashPrefixOrgId: false,
        icon: 'fa-shapes',
        label: 'systemfield|p',
        labelMenu: 'nav-left.section.sysadmin.systemfields',
        permissionLevel: Permission.SuperAdmin,
        showHeadingButtons: true,
    },
    {
        id: 'systemfieldsexport',
        component: {name: 'component/systemfields/wizard/export'},
        hash: 'systemfieldsexport',
        hashPrefixOrgId: false,
        icon: 'fa-download',
        label: 'wizard for downloading|subject:((systemfield|p|ac))',
        permissionLevel: Permission.SuperAdmin,
    },
    {
        id: 'systemfieldsimport',
        component: {name: 'component/systemfields/wizard/import'},
        hash: 'systemfieldsimport',
        hashPrefixOrgId: false,
        icon: 'fa-upload',
        label: 'wizard for uploading|subject:((systemfield|p|ac))',
        permissionLevel: Permission.SuperAdmin,
    },
    {
        id: 'systemfieldscopyall',
        component: {name: 'component/systemfields/copy/all'},
        hash: 'systemfieldscopyall',
        hashPrefixOrgId: false,
        icon: 'fa-copy',
        label: 'copy subject|s:((systemfield|p|ac))',
        parent: 'systemfieldsdetails',
        permissionLevel: Permission.SuperAdmin,
    },

    /*******************************************************************************************************************
     * SYNC
     ******************************************************************************************************************/
    {
        id: 'sync',
        component: {name: 'component/page-shell/details'},
        create: 'createsynctemplate',
        hash: 'o/{orgnr}/sync',
        icon: 'fa-repeat',
        fetchRouteDef: Fetch.Company,
        label: 'synchronization',
        labelMenu: 'nav-left.section.automation.sync',
        level: 1,
        permissionLevel: Permission.SuperManager,
        tabs: [
            'syncstatusv2',
            'syncexportqueue',
            


            'importhistorysearch',
            'synctemplatesearch',
            'syncintegrationtests',
        ],
        showHeadingButtons: false,
        subItems: ['importhistorydetails', 'synctemplatedetails'],
    },
    {
        id: 'syncstatusv2',
        component: {name: 'component/sync/status-v2/details'},
        // Canonical under sync/; syncv2 kept for bookmarks.
        hash: ['o/{orgnr}/sync/status/:companysyncstepid:', 'o/{orgnr}/syncv2/:companysyncstepid:'],
        icon: 'fa-repeat',
        label: 'Status',
        labelLong: 'syncstatus.heading',
        level: 1,
        parent: 'sync',
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'syncexportqueue',
        component: {name: 'component/sync/export-queue/details'},
        // Canonical under sync/; bare exportqueue kept for bookmarks.
        hash: ['o/{orgnr}/sync/exportqueue', 'o/{orgnr}/exportqueue'],
        icon: 'fa-list',
        label: 'sync.tab.exportqueue',
        parent: 'sync',
        permissionLevel: Permission.SuperManager,
    },
    // Route stays outside DEBUG (URL-reachable in prod); the menu tab in `sync.tabs` stays DEBUG-gated.
    {
        id: 'synccredentials',
        component: {name: 'component/sync/credentials/details'},
        // Canonical under sync/; synccredentials kept for bookmarks.
        hash: ['o/{orgnr}/sync/credentials', 'o/{orgnr}/synccredentials'],
        icon: 'fa-key',
        label: 'sync.tab.credentials',
        parent: 'sync',
        permissionLevel: Permission.CompanyAdmin,
    },


    /*******************************************************************************************************************
     * SYNCTEMPLATE
     ******************************************************************************************************************/
    {
        id: 'synctemplatesearch',
        child: 'synctemplatedetails',
        component: {name: 'component/export-template/search'},
        create: 'createsynctemplate',
        // Canonical under sync/template/; flat synctemplate paths kept for bookmarks.
        hash: [
            'o/{orgnr}/sync/template/search',
            'o/{orgnr}/sync/template',
            'o/{orgnr}/synctemplate/search',
            'o/{orgnr}/synctemplate',
        ],
        icon: 'fa-sync-template',
        label: 'template|p',
        isSearch: true,
        parent: 'sync',
        permissionLevel: Permission.SuperManager,
        serviceName: 'searchexporttemplate',
        showHeading: false,
        subItems: null,
    },
    {
        id: 'syncintegrationtests',
        component: {name: 'component/sync/integration-tests/details'},
        hash: 'o/{orgnr}/sync/integrationtests',
        icon: 'fa-play',
        label: 'sync.tab.integrationstests',
        parent: 'sync',
        permissionLevel: Permission.OrganizationAdmin,
    },
    {
        id: 'synctemplatedetails',
        component: {name: 'component/page-shell/details'},
        content: 'component/export-template/details',
        edit: 'editsynctemplate',
        fetchRouteDef: Fetch.SyncTemplate,
        // Canonical under sync/template/; flat synctemplate path kept for bookmarks.
        hash: ['o/{orgnr}/sync/template/{id}', 'o/{orgnr}/synctemplate/{id}'],
        icon: 'fa-sync-template',
        isSubitem: true,
        label: 'synchronization template',
        permissionLevel: Permission.SuperManager,
        parent: 'sync',
        tabs: ['synctemplateinfo', 'synctemplateparentpath', 'synctemplatevisma', 'synctemplateexportmappings', 'synctemplateimportmappings'],
    },
    {
        // Legacy bookmark alias for double-orgnr synctemplate URLs (prefer synctemplatedetails).
        id: 'synctemplatedetailsorg',
        _extend: 'synctemplatedetails',
        hash: [
            'o/{orgnr}/sync/template/{duporgnr}/{id}',
            'o/{orgnr}/synctemplate/{duporgnr}/{id}',
        ],
    },
    {
        id: 'synctemplateinfo',
        icon: 'fa-info-circle',
        label: 'information',
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'synctemplateparentpath',
        icon: 'fa-folder-upload',
        label: 'parent path',
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'synctemplatevisma',
        icon: 'fa-file-export',
        label: 'Visma',
        labelLong: 'write back to visma',
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'synctemplateexportmappings',
        icon: 'fa-cloud-upload',
        label: 'export',
        labelLong: 'exportattribute|p',
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'synctemplateimportmappings',
        icon: 'fa-cloud-download',
        label: 'import',
        labelLong: 'importattribute|p',
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'createsynctemplate',
        component: {name: 'component/page-shell/details'},
        content: 'component/export-template/create',
        hash: 'o/{orgnr}/createsynctemplate',
        icon: 'fa-plus',
        label: 'sync.template.create',
        parent: 'sync',
        permissionLevel: Permission.CompanyAdmin,
        tabMemory: 'synctemplatedetails',
        tabs: ['synctemplateinfo', 'synctemplateparentpath', 'synctemplatevisma', 'synctemplateexportmappings', 'synctemplateimportmappings'],
    },
    {
        id: 'editsynctemplate',
        component: {name: 'component/page-shell/details'},
        content: 'component/export-template/edit',
        fetchRouteDef: Fetch.SyncTemplate,
        hash: 'o/{orgnr}/editsynctemplate/{id}',
        icon: 'fa-pencil',
        label: 'edit',
        labelLong: 'edit subject|s:((synchronization template|ac))',
        parent: 'synctemplatedetails',
        permissionLevel: Permission.CompanyAdmin,
        tabMemory: 'synctemplatedetails',
        tabs: ['synctemplateinfo', 'synctemplateparentpath', 'synctemplatevisma', 'synctemplateexportmappings', 'synctemplateimportmappings'],
    },
    {
        id: 'importhistorysearch',
        child: 'importhistorydetails',
        component: {name: 'component/import/search'},
        // Canonical under sync/importhistory/; flat paths kept for bookmarks.
        hash: [
            'o/{orgnr}/sync/importhistory/search',
            'o/{orgnr}/sync/importhistory',
            'o/{orgnr}/importhistory/search',
            'o/{orgnr}/importhistory',
        ],
        icon: 'fa-import-history',
        label: 'History',
        isSearch: true,
        parent: 'sync',
        permissionLevel: Permission.SuperManager,
        serviceName: 'searchimporthrfile',
        subItems: null,
    },
    {
        id: 'importhistorydetails',
        component: {name: 'component/page-shell/details'},
        content: 'component/import/details',
        fetchRouteDef: Fetch.ImportHrFile,
        // Canonical under sync/importhistory/; flat path kept for bookmarks.
        hash: [
            'o/{orgnr}/sync/importhistory/{importfileid}/:options:',
            'o/{orgnr}/importhistory/{importfileid}/:options:',
        ],
        icon: 'fa-import-history',
        isSubitem: true,
        label: 'import history',
        parent: 'importhistorysearch',
        permissionLevel: Permission.SuperManager,
        showHeadingButtons: false,
        tabs: ['importhistoryinfo', 'importhistorysync', 'importhistoryhistory', 'importhistorylog'],
    },
    {id: 'importhistoryinfo', label: 'information', icon: 'fa-info-circle', permissionLevel: Permission.SuperManager},
    {
        id: 'importhistorysync',
        label: 'synchronization cycle',
        icon: 'fa-synccycle',
        permissionLevel: Permission.SuperManager
    },
    {id: 'importhistorylog', label: 'import log', icon: 'fa-list-alt', permissionLevel: Permission.SuperManager},
    {id: 'importhistoryhistory', label: 'changelog', icon: 'fa-history', permissionLevel: Permission.SuperManager},
    {
        id: 'exportflow',
        component: {name: 'component/export-template/export/mapping'},
        fetchRouteDef: {serviceName: 'getexporttemplate'},
        label: 'export attribute',
        // Canonical under sync/template/; flat export/ path kept for bookmarks.
        hash: [
            'o/{orgnr}/sync/template/{id}/export/mapping/{mappingid}',
            'o/{orgnr}/export/{id}/mapping/{mappingid}',
        ],
        icon: 'fa-play',
        parent: 'synctemplatedetails',
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'exportflows',
        _extend: 'exportflow',
        labelLong: 'export attribute|p',
        hash: [
            'o/{orgnr}/sync/template/{id}/export/mapping',
            'o/{orgnr}/export/{id}/mapping',
        ],
        icon: 'fa-forward',
        label: 'export attribute|p',
    },
    {
        id: 'exportsyncstep',
        component: {name: 'component/export-template/export/syncstep'},
        label: 'Export synchronization step',
        hash: ['o/{orgnr}/sync/exportsyncstep/{id}', 'o/{orgnr}/exportsyncstep/{id}'],
        icon: 'fa-play',
        parent: 'sync',
        permissionLevel: Permission.SuperAdmin,
    },
    {
        id: 'testimportfile',
        component: {name: 'component/import/test'},
        label: 'test import file',
        hash: ['o/{orgnr}/sync/testimportfile/{id}', 'o/{orgnr}/testimportfile/{id}'],
        parent: 'importhistorysearch',
        permissionLevel: Permission.OrganizationAdmin,
    },
    {
        id: 'testexportp360',
        component: {name: 'component/system/testexportp360'},
        label: 'testexport|subject:Public 360°',
        hash: 'o/{orgnr}/testexportp360/{id}',
        icon: 'fa-test-export',
        permissionLevel: Permission.SuperAdmin,
    },
    {
        id: 'importp360roles',
        component: {name: 'component/system/importp360roles'},
        label: 'importatob|a:((role|p))|b:Public 360°',
        hash: 'o/{orgnr}/importp360roles/{id}',
        icon: 'fa-import-users',
        permissionLevel: Permission.SuperAdmin,
    },
    {
        id: 'executesyncstep',
        component: {name: 'component/system/execute-syncstep'},
        label: 'Execute syncstep',
        hash: ['o/{orgnr}/sync/executesyncstep/{id}', 'o/{orgnr}/executesyncstep/{id}'],
        parent: 'sync',
        permissionLevel: Permission.CompanyAdmin,
    },

    /*******************************************************************************************************************
     * SYSTEM ACCESS AND PERMISSION
     ******************************************************************************************************************/
    {
        id: 'systemaccesstart',
        component: {name: 'component/page-shell/details'},
        create: 'createsystemaccess',
        icon: 'fa-server-check-circle',
        label: 'permission|p',
        labelMenu: 'nav-left.section.accessmanagement.permissions',
        // Canonical overview path; /systemaccesstart kept for bookmarks.
        hash: [
            'o/{orgnr}/systemaccess',
            'o/{orgnr}/systemaccesstart',
        ],
        permissionLevel: Permission.SuperManager,
        showHeadingButtons: false,
        subItems: ['systemaccessdetails'],
        tabs: ['systemaccessearch', 'systempermissionsearch'],
    },
    {
        id: 'systemaccessearch',
        component: {name: 'component/systemaccess/search'},
        child: 'systemaccessdetails',
        create: 'createsystemaccess',
        fetchRouteDef: Fetch.SystemAccess,
        icon: 'fa-server-check-circle',
        label: 'collection|p',
        isSearch: true,
        hash: 'o/{orgnr}/systemaccess/collections',
        permissionLevel: Permission.SuperManager,
        showHeading: false,
        serviceName: 'searchsystem',
        subItems: null,
    },
    {
        id: 'systempermissionsearch',
        component: {name: 'component/systemaccess/roles/search/details'},
        child: 'systemaccessdetails',
        icon: 'fa-id-card-alt',
        fetchRouteDef: Fetch.SystemAccessPermission,
        label: 'permission|p',
        isSearch: true,
        // Canonical sibling under systemaccess; flat /systempermission kept for bookmarks.
        hash: [
            'o/{orgnr}/systemaccess/permissions',
            'o/{orgnr}/systempermission',
        ],
        permissionLevel: Permission.SuperManager,
        subItems: null,
    },
    {
        id: '_systemaccess',
        component: {name: 'component/page-shell/details'},
        permissionLevel: Permission.SuperManager,
        tabDefault: 'systemaccessroles',
        tabMemory: 'systemaccessdetails',
        tabs: ['systemaccessinfo', 'systemaccessroles'],
    },
    {
        id: 'systemaccessdetails',
        _extend: '_systemaccess',
        content: 'component/systemaccess/details',
        edit: 'editsystemaccess',
        fetchRouteDef: Fetch.SystemAccess,
        hash: 'o/{orgnr}/systemaccess/{id}',
        icon: 'fa-server-check-circle',
        isSubitem: true,
        label: 'accessmanagement.collection.view',
        parent: 'systemaccesstart',
        showHeadingButtons: false,
    },
    {
        id: 'createsystemaccess',
        _extend: '_systemaccess',
        content: 'component/systemaccess/create',
        icon: 'fa-plus',
        label: 'accessmanagement.collection.create',
        hash: 'o/{orgnr}/createsystemaccess',
        parent: 'systemaccesstart',
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'editsystemaccess',
        _extend: '_systemaccess',
        content: 'component/systemaccess/edit',
        fetchRouteDef: Fetch.SystemAccess,
        hash: 'o/{orgnr}/editsystemaccess/{id}',
        icon: 'fa-pencil',
        label: 'accessmanagement.collection.edit',
        parent: 'systemaccessdetails',
        permissionLevel: Permission.CompanyAdmin,
    },
    {id: 'systemaccessinfo', icon: 'fa-info-circle', label: 'information', permissionLevel: Permission.SuperManager},
    {id: 'systemaccessroles', icon: 'fa-id-card-alt', label: 'permission|p', permissionLevel: Permission.SuperManager},
    {
        id: 'wizardpermissions',
        component: {name: 'component/permission/wizard'},
        label: 'assign permission|p',
        labelMenu: 'nav-left.section.accessmanagement.assignpermissions',
        labelLong: 'wizard for assigning permissions',
        // Canonical omits trailing orgnr; double-orgnr (duporgnr) kept for bookmarks.
        hash: [
            'o/{orgnr}/wizardpermissions/:systemid:/:roleid:',
            'o/{orgnr}/wizardpermissions/{duporgnr}/:systemid:/:roleid:',
        ],
        icon: 'fa-map-signs-id-card',
        objectTypeId: ObjectTypeId.User,
        parent: 'systemaccesstart',
        permissionLevel: Permission.SuperEmployee,
    },
    {
        id: 'systemaccesswizardgenerate-rs-and-mf',
        component: {name: 'component/systemaccess/wizard/generate-rs-and-mf/main'},
        icon: 'fa-map-signs',
        label: 'generate ruleset and message flow',
        hash: 'o/{orgnr}/systemaccess/wizard/generaterulesetandmessageflow/:systemid:/:systemroleid:',
        parent: 'systemaccesstart',
    },

    /*******************************************************************************************************************
     * MESSAGE FLOW
     ******************************************************************************************************************/
    {
        id: 'messageflowsearch',
        component: {name: 'component/messages/search-select'},
        child: 'messageflowdetails',
        create: 'createmessageflow',
        description: 'messageflows.search.description',
        hash: ['o/{orgnr}/messageflow/search', 'o/{orgnr}/messageflow'],
        icon: 'fa-message-flow',
        label: 'messageflow|p',
        labelMenu: 'nav-left.section.automation.messageflows',
        isSearch: true,
        objectTypeId: ObjectTypeId.Message,
        permissionLevel: Permission.SuperManager,
        serviceName: 'searchmessage',
        showHeadingButtons: false,
        subItems: null,
    },
    {
        id: 'messageflowdetails',
        component: {name: 'component/page-shell/details'},
        content: 'component/messages/details',
        edit: 'editmessageflow',
        fetchRouteDef: Fetch.MessageFlow,
        hash: 'o/{orgnr}/messageflow/{id}',
        icon: 'fa-message-flow',
        isSubitem: true,
        label: 'messageflow',
        parent: 'messageflowsearch',
        permissionLevel: Permission.SuperManager,
        showHeadingButtons: false,
        tabs: ['messageflowdetailsinfo', 'messageflowdetailslog', 'sendmessage'],
    },
    {
        id: 'messageflowdetailsinfo',
        description: 'messageflows.messagelfow.information.description',
        icon: 'fa-info-circle',
        label: 'messageflows.messageflow.information',
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'messageflowdetailslog',
        description: 'messageflows.messageflow.log.description',
        icon: 'fa-list-alt',
        label: 'messageflows.messageflow.log',
        permissionLevel: Permission.SuperManager,
        level: 2,
        serviceName: 'searchmessagelog',
        showHeading: false,
    },
    {
        id: 'createmessageflow',
        component: {name: 'component/messages/create'},
        label: 'messageflows.messageflow.create',
        hash: 'o/{orgnr}/createmessageflow',
        icon: 'fa-plus',
        parent: 'messageflowsearch',
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'createmessageflowehub',
        _extend: 'createmessageflow',
        component: {name: 'component/messages/createehub'},
        hash: 'o/{orgnr}/createmessageflowehub/{formid}',
        showAppInteract: false,
        storeHash: false,
    },
    {
        id: 'editmessageflow',
        component: {name: 'component/messages/edit'},
        fetchRouteDef: Fetch.MessageFlow,
        label: 'edit subject|s:((messageflow|ac))',
        hash: 'o/{orgnr}/editmessageflow/{id}',
        icon: 'fa-pencil',
        parent: 'messageflowdetails',
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'editmessageflowehub',
        _extend: 'editmessageflow',
        component: {name: 'component/messages/editehub'},
        hash: 'o/{orgnr}/editmessageflowehub/{id}',
        showAppInteract: false,
        storeHash: false,
    },
    {
        id: 'deletemessageflowehub',
        component: {name: 'component/messages/deleteehub'},
        hash: 'o/{orgnr}/deletemessageflowehub/{id}',
        label: 'delete subject|s:((messageflow|ac))',
        parent: 'messageflowsearch',
        permissionLevel: Permission.CompanyAdmin,
        showAppInteract: false,
        storeHash: false,
    },
    {
        id: 'sendmessage',
        description: 'messageflows.messageflow.send.description',
        fetchRouteDef: Fetch.MessageFlow,
        label: 'messageflows.messageflow.send',
        parent: 'messageflowsearch',
        permissionLevel: Permission.SuperManager,
        showHeading: false,
    },

    /*******************************************************************************************************************
     * EHUB
     ******************************************************************************************************************/
    {
        id: '_ehub',
        level: 1,
        license: 'ehub',
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'ehub',
        _extend: '_ehub',
        component: {name: 'component/page-shell/details'},
        hash: 'o/{orgnr}/ehub',
        icon: 'fa-puzzle-piece',
        label: 'eHub',
        labelMenu: 'nav-left.section.automation.ehub',
        license: 'ehub',
        permissionLevel: Permission.LoggedIn,
        subItems: ['formdatadetails', 'formtemplatedetails'],
        tabs: ['availableforms', 'formdatasearch', 'formtemplatesearch'],
    },
    {
        id: 'availableforms',
        component: {name: 'component/form/template/available/search'},
        hash: 'o/{orgnr}/availableforms',
        icon: 'fa-file-invoice',
        label: 'form|p',
        isSearch: true,
        license: 'ehub',
        parent: 'ehub',
        permissionLevel: Permission.LoggedIn,
        serviceName: 'searchformtemplate',
        subItems: null,
    },
    {
        id: 'formtemplatesearch',
        child: 'formtemplatedetailsv',
        component: {name: 'component/form/template/search'},
        hash: ['o/{orgnr}/formtemplate/search', 'o/{orgnr}/formtemplate'],
        icon: 'fa-form-template',
        label: 'form template|p',
        isSearch: true,
        license: 'ehub',
        parent: 'ehub',
        permissionLevel: Permission.CompanyAdmin,
        serviceName: 'searchformtemplate',
        subItems: null,
    },
    {
        id: 'formtemplatedetailsv',
        component: {name: 'component/form/template/read/versionredirect'},
        hash: 'o/{orgnr}/formtemplatev/{id}/:version:/:mode:/:targetid:',
        icon: 'fa-form-template',
        label: 'form',
        license: 'ehub',
        parent: 'formtemplatesearch',
        permissionLevel: Permission.LoggedIn,
        showHeading: false,
    },
    {
        id: 'formtemplatedetails',
        component: {name: 'component/form/template/read/details'},
        edit: 'editformtemplate',
        fetchRouteDef: Fetch.FormTemplate,
        hash: 'o/{orgnr}/formtemplate/{id}',
        icon: 'fa-form-template',
        isSubitem: true,
        label: 'formtemplate',
        license: 'ehub',
        parent: 'formtemplatesearch',
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'createformtemplate',
        component: {name: 'component/form/template/create/details'},
        hash: 'o/{orgnr}/createformtemplate',
        icon: 'fa-plus',
        label: 'create s|s:((formtemplate|ac))',
        license: 'ehub',
        parent: 'formtemplatesearch',
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'editformtemplate',
        component: {name: 'component/form/template/edit/details'},
        fetchRouteDef: Fetch.FormTemplate,
        hash: 'o/{orgnr}/editformtemplate/{id}',
        icon: 'fa-pencil',
        label: 'edit subject|s:((formtemplate|ac))',
        license: 'ehub',
        parent: 'formtemplatedetails',
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'formview',
        component: {name: 'component/form/view/details'},
        errorComponents: {'not_logged_in': 'component/form/view/details'},
        // Standalone remains primary (task URLs / win.html). Legacy double-orgnr kept for bookmarks.
        hash: [
            'formview/{orgnr}/{id}/:targetid:/:preview:',
            'o/{orgnr}/formview/{duporgnr}/{id}/:targetid:/:preview:',
        ],
        hashPrefixOrgId: false,
        label: 'form',
        permissionLevel: Permission.LoggedIn,
        storeHash: true,
    },
    {
        id: 'formview_v2',
        component: {name: 'component/form/view/view-v2'},
        errorComponents: {'not_logged_in': 'component/form/view/details'},
        hash: [
            'formview2/{orgnr}/{id}/:targetid:/:preview:',
            'o/{orgnr}/formview2/{duporgnr}/{id}/:targetid:/:preview:',
        ],
        hashPrefixOrgId: false,
        permissionLevel: Permission.LoggedIn,
        showAppInteract: false,
        storeHash: false,
    },
    {
        id: '_formdata',
        icon: 'fa-file-invoice',
        label: 'formdata',
        license: 'ehub',
        manual: false,
        objectTypeId: ObjectTypeId.FormData,
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'formdatasearch',
        _extend: '_formdata',
        child: 'formdatadetails',
        component: {name: 'component/form/instance/search'},
        hash: 'o/{orgnr}/formdatasearch',
        isSearch: true,
        parent: 'ehub',
        permissionLevel: Permission.DepartmentAdmin,
        serviceName: 'searchobject',
        subItems: ['formdatadetails'],
    },
    {
        id: 'formdatadetails',
        _extend: '_formdata',
        component: {name: 'component/page-shell/details'},
        content: 'component/page-shell/tab-panels',
        fetchRouteDef: Fetch.FormData,
        hash: 'o/{orgnr}/formdata/{id}',
        isSubitem: true,
        parent: 'formdatasearch',
        permissionLevel: Permission.DepartmentAdmin,
        tabs: ['formdatasourcedata', 'formdatacontributors'],
    },
    // TODO: the API doesn't support edit of form data yet.
    // {
    //     id: 'editformdata',
    //     _extend: '_editobject',
    //     fetchRouteDef: Fetch.FormData,
    //     hash: 'o/{orgnr}/editformdata/{id}',
    //     label: 'edit subject|s:((formdata|ac))',
    //     objectTypeId: ObjectTypeId.FormData,
    //     parent: 'formdatadetails',
    //     permissionLevel: Permission.SuperManager,
    // },
    {
        id: '_externalcontact',
        label: 'external contact|p',
        license: 'ehub',
        objectTypeId: ObjectTypeId.ExternalContact,
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'externalcontactsearch',
        _extend: ['_externalcontact', 'usersearch'],
        isSearch: true,
    },

    /*******************************************************************************************************************
     * SETTINGS
     ******************************************************************************************************************/
    {
        id: 'settings',
        component: {name: 'component/page-shell/details'},
        content: 'component/settings/details',
        icon: 'fa-cog',
        label: 'setting|p',
        hash: 'settings',
        hashPrefixOrgId: false,
        permissionLevel: Permission.LoggedIn,
        tabs: ['settingsgeneral', 'settingsuser', 'settingsgroup', 'settingsdepartment', 'settingsformdata', 'settingsexternalcontact', 'settingsapiuser'],
    },
    {
        id: '_settings',
        permissionLevel: Permission.LoggedIn,
    },
    {
        id: 'settingsgeneral',
        _extend: '_settings',
        icon: 'fa-cogs',
        label: 'general',
        permissionLevel: Permission.LoggedIn,
    },
    {
        id: 'settingsuser',
        _extend: '_settings',
        hash: 'settingsuser',
        hashPrefixOrgId: false,
        icon: 'fa-user',
        label: 'user',
        labelLong: '((user)) ((search|ac))',
        permissionLevel: Permission.SuperEmployee,
    },
    {
        id: 'settingsgroup',
        _extend: '_settings',
        hash: 'settingsgroup',
        hashPrefixOrgId: false,
        icon: 'fa-users',
        label: 'group',
        labelLong: '((group)) ((search|ac))',
        permissionLevel: Permission.SuperManager,
    },
    {
        id: 'settingsdepartment',
        _extend: '_settings',
        hash: 'settingsdepartment',
        hashPrefixOrgId: false,
        icon: 'fa-sitemap',
        label: 'department',
        labelLong: '((department)) ((search|ac))',
        permissionLevel: Permission.DepartmentAdmin,
    },
    {
        id: 'settingsformdata',
        _extend: '_settings',
        icon: 'fa-file-invoice',
        label: 'formdata',
        labelLong: '((formdata)) ((search|ac))',
        license: 'ehub',
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'settingsexternalcontact',
        _extend: '_settings',
        label: 'externalcontact',
        labelLong: '((externalcontact)) ((search|ac))',
        license: 'ehub',
        permissionLevel: Permission.CompanyAdmin,
    },
    {
        id: 'settingsapiuser',
        _extend: '_settings',
        icon: 'fa-external-contact',
        label: 'APIuser',
        labelLong: '((APIuser)) ((search|ac))',
        permissionLevel: Permission.CompanyAdmin,
    },

    {
        id: 'sitemap',
        component: {name: 'component/sitemap/sitemap'},
        label: 'sitemap',
        labelMenu: 'sitemap',
        hash: 'sitemap',
        hashPrefixOrgId: false,
        icon: 'fa-sitemap',
        permissionLevel: Permission.LoggedIn,
    },

    /*******************************************************************************************************************
     * LOG
     ******************************************************************************************************************/
    {
        id: 'logstart',
        component: {name: 'component/page-shell/details'},
        hash: 'o/{orgnr}/log',
        icon: 'fa-list-alt',
        label: 'log|p',
        labelMenu: 'nav-left.section.sysadmin.logs',
        permissionLevel: Permission.CompanyAdmin,
        tabs: ['logauditsearch', 'logsecuritysearch', 'logerrorsearch'],
    },
    {
        id: '_logsearch',
        icon: 'fa-list-alt',
        permissionLevel: Permission.CompanyAdmin,
        sortColumn: 'Registered',
        sortAscending: false,
    },
    {
        id: 'logauditsearch',
        _extend: '_logsearch',
        component: {name: 'component/log/audit/search'},
        hash: 'o/{orgnr}/logaudit',
        label: 'audit',
        isSearch: true,
        parent: 'logstart',
        serviceName: 'searchauditlog',
        showHeading: false,
    },
    {
        id: 'logsecuritysearch',
        _extend: '_logsearch',
        component: {name: 'component/log/security/search'},
        hash: 'o/{orgnr}/logsecurity',
        label: 'security',
        isSearch: true,
        parent: 'logstart',
        serviceName: 'searchauditlogsecurity',
        showHeading: false,
    },
    {
        id: 'logerrorsearch',
        _extend: '_logsearch',
        component: {name: 'component/log/error/search'},
        hash: 'o/{orgnr}/logerror/:searchkey:',
        label: 'error',
        isSearch: true,
        parent: 'logstart',
        serviceName: 'searchauditlogerror',
        showHeading: false,
    },

    /*******************************************************************************************************************
     * MISCELLANEOUS
     ******************************************************************************************************************/
    {
        id: 'application',
        component: {name: 'component/system/application'},
        label: 'application',
        hash: 'application',
        hashPrefixOrgId: false,
        icon: 'fa-browser',
        permissionLevel: Permission.LoggedIn,
    },
    {
        id: 'privacypolicy',
        component: {name: 'component/privacy/policy-page'},
        label: 'privacy.and.agreements',
        hash: 'o/{orgnr}/privacypolicy',
        hashPrefixOrgId: false,
        icon: 'fa-user-shield',
        permissionLevel: Permission.LoggedIn,
    },
    {
        id: 'login',
        canSeeAfterLogin: false,
        component: null,
        label: 'logging in|td',
        hash: 'login/:token:/:redirecturl*:',
        hashPrefixOrgId: false,
        permissionLevel: Permission.None,
        storeHash: false
    },
    {
        id: 'logout',
        canSeeAfterLogin: false,
        component: null,
        label: 'log out',
        hash: 'logout',
        hashPrefixOrgId: false,
        permissionLevel: Permission.None,
        storeHash: false
    },
    {
        id: 'formlogin',
        canSeeAfterLogin: false,
        component: {name: 'component/login/form'},
        label: 'login',
        hash: ['formlogin', 'formlogin/:orgnr:/:index:', 'o/{orgnr}/formlogin/:index:'],
        hashPrefixOrgId: false,
        icon: 'fa-sign-in',
        permissionLevel: Permission.None,
        storeHash: false,
    },
    {
        id: 'about',
        component: {name: 'component/page-shell/details'},
        hash: 'o/{orgnr}/about',
        hashPrefixOrgId: false,
        icon: 'fa-puzzle-piece-info',
        label: 'eAdm',
        labelMenu: 'nav-left.section.eadm.heading',
        permissionLevel: Permission.LoggedIn,
        showTabs: false,
        tabs: ['about_privacypolicy', 'about_application'],
    },
    {
        id: 'about_privacypolicy',
        _extend: 'privacypolicy',
        hash: 'o/{orgnr}/about/privacypolicy',
        hashPrefixOrgId: false,
        labelMenu: 'privacy.and.agreements',
        pageShell: 'about',
    },
    {
        id: 'about_orginfo',
        _extend: 'about_privacypolicy',
        hash: 'o/{orgnr}/about/orginfo',
        pageShell: 'about',
    },
    {
        id: 'about_application',
        _extend: 'application',
        hash: 'o/{orgnr}/about/application',
        hashPrefixOrgId: false,
        labelMenu: 'application',
        pageShell: 'about',
    },
    {
        id: 'redirect',
        component: {name: 'component/route/redirect'},
        hash: 'o/{orgnr}/redirect/{options}',
        permissionLevel: Permission.LoggedIn,
    },
    {
        id: 'http',
        canSeeAfterLogin: false,
        component: {name: 'component/http/http'},
        label: '',
        hash: 'http/{id}',
        hashPrefixOrgId: false,
        permissionLevel: Permission.None,
        storeHash: false,
    },
    {
        id: 'http400',
        canSeeAfterLogin: false,
        label: 'doesnotexist',
        linkRegexp: /.*/,
        hashPrefixOrgId: false,
        permissionLevel: Permission.None,
        component: {name: 'component/http/http', params: {id: 400}},
        storeHash: false,
    },
]

export function isRouteDefinition(id: TRouteId): boolean {
    if (typeof id === 'string') {
        return !!RouteDefinition.find(o => o.id == id);
    }
    return false;
}

/** Find a raw catalog entry whose hash matches a Crossroads pattern (no defaultsDeep). */
export function findRouteDefinitionByHashPattern(pattern: string): IRouteDefinition | undefined {
    return RouteDefinition.find((r) => {
        if (!r.hash) {
            return false;
        }
        if (typeof r.hash === 'string') {
            return r.hash === pattern;
        }
        return r.hash.includes(pattern);
    });
}

/** Raw catalog entry by id (no defaultsDeep / _extend expansion). */
export function getDef(id: TRouteId): IRouteDefinition | undefined {
    const ret = RouteDefinition.find(o => o.id == id);
    if (ret) {
        return ret;
    }
    _d.warn('No such route definition:', id);
    return undefined;
}

/** All raw catalog entries (no defaultsDeep). */
export function getRawRouteDefinitions(): readonly IRouteDefinition[] {
    return RouteDefinition;
}
